<style lang="scss" scoped>
@import 'resources/sass/v1/abstracts/_variables';

.faq-overview {
    display: flex;
    flex-direction: column;
    align-items: center;

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s;
    }
    .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
        opacity: 0;
    }

    .faq-container {
        display: flex;
        width: 100%;
        flex-direction: column;
    }

    .title {
        margin-bottom: 40px;
    }

    .faq-category {
        width: 100%;
        padding: 40px 0; // Equivalent to Tailwind's p-5
    }

    .faq-category-title {
    }

    .faq-list {
        margin-top: 2.5rem; // Equivalent to Tailwind's mt-8
    }

    .faq-item {
        padding-top: 1.5rem; // Equivalent to Tailwind's py-6
        padding-bottom: 1.5rem;
        border-top: 1px solid rgb(239 239 239); // Equivalent to Tailwind's divide-y divide-white/10

        &:first-child {
            padding-top: 0; // Equivalent to Tailwind's first:pt-0
            border: none;
        }

        &:last-child {
            padding-bottom: 0; // Equivalent to Tailwind's last:pb-0
        }

        .faq-item-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            text-align: left;
            cursor: pointer;
            user-select: none;
        }

        .faq-item-title {
            font-size: 1.125rem; // Equivalent to Tailwind's text-base
            font-weight: 600; // Equivalent to Tailwind's font-semibold
            line-height: 1.75rem; // Equivalent to Tailwind's text-base/7
        }

        .faq-item-icon {
            margin-left: 1.5rem; // Equivalent to Tailwind's ml-6
            display: flex;
            align-items: center;
            height: 1.75rem; // Equivalent to Tailwind's h-7

            svg {
                height: 24px;
                width: auto; // Equivalent to Tailwind's h-6
                font-size: 24px;
                color: $primary; // Equivalent to Tailwind's text-black

                path {
                    fill: currentColor;
                }
            }
        }

        .faq-item-content {
            margin-top: 1rem; // Equivalent to Tailwind's mt-4
            padding-right: 3rem; // Equivalent to Tailwind's pr-12
            font-size: 1rem; // Equivalent to Tailwind's text-base
            line-height: 1.75rem; // Equivalent to Tailwind's text-base/7

            .prose {
                max-width: none;
            }
        }
    }

    .spinner-background {
        position: relative;
        width: 100%;
        height: 300px;

        .spinner {
            position: absolute;
            z-index: 10;
            top: calc(50% - 30px);
            left: calc(50% - 30px);
            transform: translate(-50%, -50%);
        }
    }
}
</style>

<template>
    <div class="faq-overview">
        <div class="faq-container">
            <h2 v-if="title && title !== ''" class="title max-w-4xl text-primary text-24 font-bold xl:text-44">{{ title }}</h2>

            <transition-group name="fade" tag="div" class="relative">
                <div key="loading" v-if="!ready" class="spinner-background">
                    <div class="spinner"></div>
                </div>

                <template v-else>
                    <div v-for="(chunk, index) in faqItemChunks" :key="index" class="faq-category">
                        <h3 class="faq-category-title text-24 text-primary font-bold" :id="slugify(chunk.category)" v-html="chunk.category"></h3>
                        <dl class="faq-list">
                            <div v-for="(faq, index) in chunk.items" :key="index" class="faq-item">
                                <dt>
                                    <div :id="faq.id" class="faq-item-header" @click="openItems.includes(faq.id) ? openItems.splice(openItems.indexOf(faq.id), 1) : openItems.push(faq.id)">
                                        <span class="faq-item-title text-primary" v-html="faq.title.rendered"></span>
                                        <span class="faq-item-icon text-primary">
                                            <svg-vue v-if="!openItems.includes(faq.id)" icon="fontawesome/plus" aria-hidden="true"></svg-vue>
                                            <svg-vue v-else icon="fontawesome/minus"></svg-vue>
                                        </span>
                                    </div>
                                </dt>
                                <dd class="faq-item-content" v-if="openItems.includes(faq.id)">
                                    <div class="prose text-primary prose-strong:text-primary prose-lg" v-html="faq.acf.content"></div>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </template>
            </transition-group>
        </div>
    </div>
</template>

<script>
var scrollIntoView = require('scroll-into-view');

export default {
    props: {
        title: {
            type: String,
            required: false,
        },
        filter: {
            type: Array,
            required: false,
        },
    },

    data: () => ({
        ready: false,
        faqitems: null,
        faqCategories: null,
        openItems: [],
    }),

    mounted() {
        Promise.all([this.getPosts(), this.getCategories()]).then(() => {
            this.ready = true;
            this.$nextTick(() => {
                // catch hash for later smooth scrolling
                var windowhash = window.location.hash;

                // remove url parameters from hash
                if (windowhash) {
                    windowhash = windowhash.split('?')[0];
                }

                if (windowhash) {
                    this.scrollToPossibleHash(windowhash);
                }
            });
        });
    },

    methods: {
        async getPosts() {
            const response = await fetch('/api/faq');
            const data = await response.json();
            this.faqitems = data;
        },
        async getCategories() {
            const response = await fetch('/api/faq-categories');
            const data = await response.json();
            this.faqCategories = data;
        },

        slugify(input) {
            if (!input) return '';

            // make lower case and trim
            var slug = input.toLowerCase().trim();

            // remove accents from charaters
            slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

            // replace invalid chars with spaces
            slug = slug.replace(/[^a-z0-9\s-]/g, ' ').trim();

            // replace multiple spaces or hyphens with a single hyphen
            slug = slug.replace(/[\s-]+/g, '-');

            return slug;
        },

        scrollToPossibleHash(hash) {
            var hash = hash.replace('#', '');

            var section = document.getElementById(hash);

            if (!section) {
                var slug = this.slugify(decodeURI(hash), { lower: true });
                section = document.getElementById(slug);
            }

            // get the offset of the header
            var header = document.querySelector('header');
            var headerOffset = header.offsetHeight;

            scrollIntoView(section, { align: { top: 0, topOffset: headerOffset + 40 } });
        },
    },

    computed: {
        faqItemChunks() {
            if (!this.faqitems || !this.faqCategories) {
                return [];
            }

            // Create an array to store chunks
            const chunked = [];

            this.faqitems.forEach((item) => {
                const categoryIds = item['faq-category'];

                if (!categoryIds || !categoryIds.length) {
                    return;
                }

                if (this.categoryFilter && !this.categoryFilter.map((filter) => filter.term_id).includes(categoryIds[0])) {
                    return;
                }

                // Find the category name
                const category = this.faqCategories.find((cat) => cat.id === categoryIds[0]);
                if (!category) {
                    return;
                }

                // Find or create the chunk for this category
                let categoryChunk = chunked.find((chunk) => chunk.category === category.name);
                if (!categoryChunk) {
                    categoryChunk = { category: category.name, items: [] };
                    chunked.push(categoryChunk);
                }

                // Add the item to the category chunk
                categoryChunk.items.push(item);

                
            });

            return chunked;
        },
    },
};
</script>
